import { definePreset, updatePreset } from '@primeuix/themes';
import type { ComponentsDesignTokens, Preset } from '@primeuix/themes/types';
import { KDSPrimengThemePresetColorOverrides } from '../models/primeng-theme-preset.model';
import { KDSPrimengColorTokens } from '../constants/primeng-color-tokens.const';
import { KDSPrimengComponents } from '../constants/primeng-components.const';

/**
 * Builds the KDS PrimeNG theme preset, optionally merging caller-supplied overrides.
 *
 * Both light and dark color schemes map to the same KDS semantic CSS custom-property tokens by default,
 * so actual color values are resolved at runtime from `data-theme`-scoped CSS variables.
 * Pass `overrides` to replace specific token values for a given application.
 *
 * @param colorOverrides - Optional deep-partial color-scheme overrides applied on top of the KDS base preset via `updatePreset`.
 *   Accepts any subset of `{ light, dark }` token trees — all nesting levels are optional.
 * @returns A fully resolved {@link Preset}.
 */
export const getKDSPrimengThemePreset = (colorOverrides?: KDSPrimengThemePresetColorOverrides): Preset => {
  const basePreset = definePreset({
    semantic: {
      colorScheme: {
        light: KDSPrimengColorTokens,
        dark: KDSPrimengColorTokens,
      },
    },
    components: KDSPrimengComponents as unknown as ComponentsDesignTokens,
  });

  if (!colorOverrides) {
    return basePreset;
  }

  return updatePreset(basePreset, { semantic: { colorScheme: colorOverrides } });
};
